<script setup>
import { onMounted, onBeforeUnmount, computed, ref, reactive, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { directive as vOnClickaway } from 'vue3-click-away';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import { useUISettings } from 'dashboard/composables/useUISettings';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

import ContactsListLayout from 'dashboard/components-next/Contacts/ContactsListLayout.vue';
import SzContactsEmpty from 'dashboard/components-next/Contacts/EmptyState/SzContactsEmpty.vue';
import CreateNewContactDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactDialog.vue';
import SzImportContactsModal from 'dashboard/components/widgets/conversation/solarz/SzImportContactsModal/SzImportContactsModal.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactsList from 'dashboard/components-next/Contacts/Pages/ContactsList.vue';
import ContactsBulkActionBar from '../components/ContactsBulkActionBar.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import BulkActionsAPI from 'dashboard/api/bulkActions';
// SZ: filter sheet de CONTATOS — versão adaptada (etiquetas + bloqueados + período).
// Mesmo visual do Figma mas só com campos que fazem sentido pra contato.
import SzContactsFilterSheet from 'dashboard/components-next/Contacts/ContactsHeader/SzContactsFilterSheet.vue';

const DEFAULT_SORT_FIELD = 'last_activity_at';
const DEBOUNCE_DELAY = 300;

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { updateUISettings, uiSettings } = useUISettings();

const contacts = useMapGetter('contacts/getContactsList');
const uiFlags = useMapGetter('contacts/getUIFlags');
const customViewsUiFlags = useMapGetter('customViews/getUIFlags');
const segments = useMapGetter('customViews/getContactCustomViews');
const appliedFilters = useMapGetter('contacts/getAppliedContactFilters');
const meta = useMapGetter('contacts/getMeta');

const searchQuery = computed(() => route.query?.search);
const searchValue = ref(searchQuery.value || '');
const pageNumber = computed(() => Number(route.query?.page) || 1);

const parseSortSettings = (sortString = '') => {
  const hasDescending = sortString.startsWith('-');
  const sortField = hasDescending ? sortString.slice(1) : sortString;
  return {
    sort: sortField || DEFAULT_SORT_FIELD,
    order: hasDescending ? '-' : '',
  };
};

const { contacts_sort_by: contactSortBy = '' } = uiSettings.value ?? {};
const { sort: initialSort, order: initialOrder } =
  parseSortSettings(contactSortBy);

const sortState = reactive({
  activeSort: initialSort,
  activeOrdering: initialOrder,
});

const activeLabel = computed(() => route.params.label);
const activeSegmentId = computed(() => route.params.segmentId);
const isFetchingList = computed(
  () => uiFlags.value.isFetching || customViewsUiFlags.value.isFetching
);
const currentPage = computed(() => Number(meta.value?.currentPage));
const totalItems = computed(() => meta.value?.count);

// SZ: infinite scroll — controla o sentinel, loadMore e estado de "fetching more"
const PER_PAGE = 15;
const totalPages = computed(() => Math.ceil((totalItems.value || 0) / PER_PAGE) || 1);
const hasMorePages = computed(() => currentPage.value < totalPages.value);
const isFetchingMore = computed(() => Boolean(uiFlags.value?.isFetchingMore));
const infiniteScrollSentinel = ref(null);
let _ioObserver = null;
const loadMore = async () => {
  if (isFetchingMore.value) return;
  if (!hasMorePages.value) return;
  const next = (currentPage.value || 1) + 1;
  try {
    await store.dispatch('contacts/getMore', {
      page: next,
      sortAttr: buildSortAttr(),
      label: activeLabel.value,
    });
  } catch (e) { /* swallow */ }
};

const selectedContactIds = ref([]);
const isBulkActionLoading = ref(false);
const bulkDeleteDialogRef = ref(null);
const selectedCount = computed(() => selectedContactIds.value.length);
const bulkDeleteDialogTitle = computed(() =>
  selectedCount.value > 1
    ? t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.TITLE')
    : t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.SINGULAR_TITLE')
);
const bulkDeleteDialogDescription = computed(() =>
  selectedCount.value > 1
    ? t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.DESCRIPTION', {
        count: selectedCount.value,
      })
    : t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.SINGULAR_DESCRIPTION')
);
const bulkDeleteDialogConfirmLabel = computed(() =>
  selectedCount.value > 1
    ? t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.CONFIRM_MULTIPLE')
    : t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.CONFIRM_SINGLE')
);
const hasSelection = computed(() => selectedCount.value > 0);
const activeSegment = computed(() => {
  if (!activeSegmentId.value) return undefined;
  return segments.value.find(view => view.id === Number(activeSegmentId.value));
});

const hasContacts = computed(() => contacts.value.length > 0);
const isContactIndexView = computed(
  () => route.name === 'contacts_dashboard_index' && pageNumber.value === 1
);
const isActiveView = computed(() => route.name === 'contacts_dashboard_active');
const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length > 0;
});
// SZ_CONTACTS_FILTER_BADGE_20260528: contagem de filtros ativos por categoria
// (Busca + Etiquetas + Período + Bloqueado). Busca conta como 1.
const activeFilterCount = computed(() => {
  let n = 0;
  if (searchValue.value) n += 1;
  const keys = new Set(
    (appliedFilters.value || []).map(f => {
      const k = f.attribute_key || f.attributeKey;
      return k === 'created_at' || k === 'last_activity_at' ? 'period' : k;
    })
  );
  n += keys.size;
  return n;
});

const showEmptyStateLayout = computed(() => {
  return (
    !searchQuery.value &&
    !hasContacts.value &&
    isContactIndexView.value &&
    !hasAppliedFilters.value
  );
});
const showEmptyText = computed(() => {
  return (
    (searchQuery.value ||
      hasAppliedFilters.value ||
      !isContactIndexView.value) &&
    !hasContacts.value
  );
});

// SZ_CONTACTS_COL_TITLE_20260528: título fixo da coluna da lista = "Todos os
// contatos" (busca/index). Mantém nome de segmento/etiqueta quando aplicável.
const listColumnTitle = computed(() => {
  if (activeSegmentId.value) return activeSegment.value?.name || 'Todos os contatos';
  if (activeLabel.value) return `#${activeLabel.value}`;
  return 'Todos os contatos';
});
const headerTitle = computed(() => {
  if (searchQuery.value) return t('CONTACTS_LAYOUT.HEADER.SEARCH_TITLE');
  if (isActiveView.value) return t('CONTACTS_LAYOUT.HEADER.ACTIVE_TITLE');
  if (activeSegmentId.value) return activeSegment.value?.name;
  if (activeLabel.value) return `#${activeLabel.value}`;
  return t('CONTACTS_LAYOUT.HEADER.TITLE');
});

const emptyStateMessage = computed(() => {
  if (isActiveView.value)
    return t('CONTACTS_LAYOUT.EMPTY_STATE.ACTIVE_EMPTY_STATE_TITLE');
  if (!searchQuery.value || hasAppliedFilters.value)
    return t('CONTACTS_LAYOUT.EMPTY_STATE.LIST_EMPTY_STATE_TITLE');
  return t('CONTACTS_LAYOUT.EMPTY_STATE.SEARCH_EMPTY_STATE_TITLE');
});

const visibleContactIds = computed(() =>
  contacts.value.map(contact => contact.id)
);

const clearSelection = () => {
  selectedContactIds.value = [];
};

const openBulkDeleteDialog = () => {
  if (!selectedContactIds.value.length || isBulkActionLoading.value) return;
  bulkDeleteDialogRef.value?.open?.();
};

const toggleSelectAll = shouldSelect => {
  selectedContactIds.value = shouldSelect ? [...visibleContactIds.value] : [];
};

const toggleContactSelection = ({ id, value }) => {
  const isAlreadySelected = selectedContactIds.value.includes(id);
  const shouldSelect = value ?? !isAlreadySelected;

  if (shouldSelect && !isAlreadySelected) {
    selectedContactIds.value = [...selectedContactIds.value, id];
  } else if (!shouldSelect && isAlreadySelected) {
    selectedContactIds.value = selectedContactIds.value.filter(
      contactId => contactId !== id
    );
  }
};

const updatePageParam = (page, search = '') => {
  const query = {
    ...route.query,
    page: page.toString(),
    ...(search ? { search } : {}),
  };

  if (!search) {
    delete query.search;
  }

  router.replace({ query });
};

const buildSortAttr = () =>
  `${sortState.activeOrdering}${sortState.activeSort}`;

const getCommonFetchParams = (page = 1) => ({
  page,
  sortAttr: buildSortAttr(),
  label: activeLabel.value,
});

const fetchContacts = async (page = 1) => {
  clearSelection();
  await store.dispatch('contacts/clearContactFilters');
  await store.dispatch('contacts/get', getCommonFetchParams(page));
  updatePageParam(page);
};

const fetchSavedOrAppliedFilteredContact = async (payload, page = 1) => {
  if (!activeSegmentId.value && !hasAppliedFilters.value) return;
  clearSelection();
  await store.dispatch('contacts/filter', {
    ...getCommonFetchParams(page),
    queryPayload: payload,
  });
  updatePageParam(page);
};

const fetchActiveContacts = async (page = 1) => {
  clearSelection();
  await store.dispatch('contacts/clearContactFilters');
  await store.dispatch('contacts/active', {
    page,
    sortAttr: buildSortAttr(),
  });
  updatePageParam(page);
};

const searchContacts = debounce(async (value, page = 1) => {
  clearSelection();
  await store.dispatch('contacts/clearContactFilters');
  searchValue.value = value;

  if (!value) {
    updatePageParam(page);
    await fetchContacts(page);
    return;
  }

  updatePageParam(page, value);
  await store.dispatch('contacts/search', {
    ...getCommonFetchParams(page),
    search: encodeURIComponent(value),
  });
}, DEBOUNCE_DELAY);

const fetchContactsBasedOnContext = async page => {
  clearSelection();
  updatePageParam(page, searchValue.value);
  if (isFetchingList.value) return;
  if (searchQuery.value) {
    await searchContacts(searchQuery.value, page);
    return;
  }
  // Reset the search value when we change the view
  searchValue.value = '';
  // If we're on the active route, fetch active contacts
  if (isActiveView.value) {
    await fetchActiveContacts(page);
    return;
  }
  // If there are applied filters or active segment with query
  if (
    (hasAppliedFilters.value || activeSegment.value?.query) &&
    !activeLabel.value
  ) {
    const queryPayload =
      activeSegment.value?.query || filterQueryGenerator(appliedFilters.value);
    await fetchSavedOrAppliedFilteredContact(queryPayload, page);
    return;
  }
  // Default case: fetch regular contacts + label
  await fetchContacts(page);
};

const assignLabels = async labels => {
  if (!labels.length || !selectedContactIds.value.length) {
    return;
  }

  isBulkActionLoading.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Contact',
      ids: selectedContactIds.value,
      labels: { add: labels },
    });
    useAlert(t('CONTACTS_BULK_ACTIONS.ASSIGN_LABELS_SUCCESS'));
    clearSelection();
    await fetchContactsBasedOnContext(pageNumber.value);
  } catch (error) {
    useAlert(t('CONTACTS_BULK_ACTIONS.ASSIGN_LABELS_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const deleteContacts = async () => {
  if (!selectedContactIds.value.length) {
    return;
  }

  isBulkActionLoading.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Contact',
      ids: selectedContactIds.value,
      action_name: 'delete',
    });
    useAlert(t('CONTACTS_BULK_ACTIONS.DELETE_SUCCESS'));
    clearSelection();
    await fetchContactsBasedOnContext(pageNumber.value);
    bulkDeleteDialogRef.value?.close?.();
  } catch (error) {
    useAlert(t('CONTACTS_BULK_ACTIONS.DELETE_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

// SZ_SORT_CYCLE_20260528: ordenação cíclica — clique alterna A→Z / Z→A /
// Mais recentes (sem dropdown). showFilterSheet permanece pro filter sheet.
const showFilterSheet = ref(false);
const SORT_CYCLE = [
  { sort: 'name', order: '', label: 'A → Z' },
  { sort: 'name', order: '-', label: 'Z → A' },
  { sort: 'last_activity_at', order: '-', label: 'Mais recentes' },
];
const sortLabel = computed(() => {
  const found = SORT_CYCLE.find(
    s => s.sort === sortState.activeSort && s.order === sortState.activeOrdering
  );
  return found ? found.label : 'Mais recentes';
});
const cycleSort = () => {
  const idx = SORT_CYCLE.findIndex(
    s => s.sort === sortState.activeSort && s.order === sortState.activeOrdering
  );
  const next = SORT_CYCLE[(idx + 1) % SORT_CYCLE.length];
  handleSort({ sort: next.sort, order: next.order });
};

// Handlers do filter sheet (placeholder — fase posterior wire com a busca real)
// SZ_CONTACTS_SEARCH_20260528: aplica a busca (prioridade) ou os filtros
// estruturados (Etiquetas/Data/Bloqueado). Busca e filtro são endpoints
// distintos no Chatwoot, então busca preenchida vence.
const onContactsFilterApply = async ({ filters = [], search = '' } = {}) => {
  showFilterSheet.value = false;
  const term = (search || '').trim();

  // 1) Busca tem prioridade — usa o endpoint de search (nome/numero/email/identifier).
  if (term) {
    await store.dispatch('contacts/clearContactFilters');
    await searchContacts(term, 1);
    return;
  }

  // 2) Sem busca: aplica filtros estruturados, se houver.
  if (Array.isArray(filters) && filters.length > 0) {
    const snake = useSnakeCase(JSON.parse(JSON.stringify(filters)));
    store.dispatch('contacts/setContactFilters', snake);
    searchValue.value = '';
    clearSelection();
    await store.dispatch('contacts/filter', {
      ...getCommonFetchParams(1),
      queryPayload: filterQueryGenerator(snake),
    });
    updatePageParam(1);
    return;
  }

  // 3) Nada preenchido: limpa e recarrega lista padrão.
  await store.dispatch('contacts/clearContactFilters');
  searchValue.value = '';
  await fetchContacts(1);
};
const onContactsFilterClear = async () => {
  showFilterSheet.value = false;
  await store.dispatch('contacts/clearContactFilters');
  searchValue.value = '';
  await fetchContacts(1);
};

const handleSort = async ({ sort, order }) => {
  Object.assign(sortState, { activeSort: sort, activeOrdering: order });

  await updateUISettings({
    contacts_sort_by: buildSortAttr(),
  });

  if (searchQuery.value) {
    await searchContacts(searchValue.value);
    return;
  }

  if (isActiveView.value) {
    await fetchActiveContacts();
    return;
  }

  await (activeSegmentId.value || hasAppliedFilters.value
    ? fetchSavedOrAppliedFilteredContact(
        activeSegmentId.value
          ? activeSegment.value?.query
          : filterQueryGenerator(appliedFilters.value)
      )
    : fetchContacts());
};

const createContact = async contact => {
  try {
    await store.dispatch('contacts/create', contact);
    createNewContactDialogRef.value?.onSuccess?.();
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SUCCESS_MESSAGE') ||
        'Contato criado.'
    );
  } catch (error) {
    useAlert(
      error?.message ||
        t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.ERROR_MESSAGE') ||
        'Falha ao criar contato.'
    );
  }
};

// SZ_CONTACTS_EMPTY_FIX_20260525: refs dos modais usados pelos cards do empty state
const createNewContactDialogRef = ref(null);
const szImportContactsModalRef = ref(null);

const openCreateNewContactDialog = async () => {
  await createNewContactDialogRef.value?.contactsFormRef?.resetValidation?.();
  createNewContactDialogRef.value?.dialogRef?.open?.();
};

const openImportContactsModal = () => {
  szImportContactsModalRef.value?.show?.();
};

const onContactsImported = () => {
  // Atualiza a lista após import bem-sucedido
  store.dispatch('contacts/get', { page: 1 }).catch(() => {});
};

watch(
  contacts,
  newContacts => {
    const idsOnPage = newContacts.map(contact => contact.id);
    selectedContactIds.value = selectedContactIds.value.filter(id =>
      idsOnPage.includes(id)
    );
  },
  { deep: true }
);

watch(hasSelection, value => {
  if (!value) {
    bulkDeleteDialogRef.value?.close?.();
  }
});

watch(
  () => uiSettings.value?.contacts_sort_by,
  newSortBy => {
    if (newSortBy) {
      const { sort, order } = parseSortSettings(newSortBy);
      sortState.activeSort = sort;
      sortState.activeOrdering = order;
    }
  },
  { immediate: true }
);

watch(
  [activeLabel, activeSegment, isActiveView],
  () => {
    fetchContactsBasedOnContext(pageNumber.value);
  },
  { deep: true }
);

watch(searchQuery, value => {
  if (isFetchingList.value) return;
  searchValue.value = value || '';
  // Reset the view if there is search query when we click on the sidebar group
  if (value === undefined) {
    if (
      isActiveView.value ||
      activeLabel.value ||
      activeSegment.value ||
      hasAppliedFilters.value
    )
      return;
    fetchContacts();
  }
});

onMounted(async () => {
  if (!activeSegmentId.value) {
    if (searchQuery.value) {
      await searchContacts(searchQuery.value, pageNumber.value);
      return;
    }
    if (isActiveView.value) {
      await fetchActiveContacts(pageNumber.value);
      return;
    }
    await fetchContacts(pageNumber.value);
  } else if (activeSegment.value && activeSegmentId.value) {
    await fetchSavedOrAppliedFilteredContact(
      activeSegment.value.query,
      pageNumber.value
    );
  }

  // SZ: setup do IntersectionObserver pro scroll infinito. O sentinel só
  // existe quando hasMorePages, então watch garante (re)criação dinâmica.
  await nextTick();
  setupInfiniteScroll();
});

const setupInfiniteScroll = () => {
  teardownInfiniteScroll();
  const el = infiniteScrollSentinel.value;
  if (!el) return;
  _ioObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) loadMore();
    });
  }, { root: null, rootMargin: '200px', threshold: 0 });
  _ioObserver.observe(el);
};
const teardownInfiniteScroll = () => {
  if (_ioObserver) { _ioObserver.disconnect(); _ioObserver = null; }
};

// Re-observa quando o sentinel monta/desmonta (após paginação ou troca de view)
watch(infiniteScrollSentinel, async el => {
  await nextTick();
  if (el) setupInfiniteScroll(); else teardownInfiniteScroll();
});

onBeforeUnmount(() => { teardownInfiniteScroll(); });
</script>

<template>
  <!-- SZ Figma: layout 2-col estilo lista de conversas.
       Coluna 1 (sz-contacts-list-col): lista de contatos.
       Coluna 2 (sz-contacts-center-col): empty state grande quando nada selecionado. -->
  <section class="sz-contacts-layout flex w-full h-full min-w-0 bg-n-background">
    <!-- Coluna 1: Lista -->
    <aside class="sz-contacts-list-col">
      <!-- SZ: filter sheet (mesmo da lista de chats) — ancorado ao .sz-contacts-list-col
           via position:absolute. Fica APENAS dentro da coluna de 360px. -->
      <SzContactsFilterSheet
        v-model="showFilterSheet"
        :initial-filters="appliedFilters || []"
        :initial-search="searchValue"
        @apply="onContactsFilterApply"
        @clear="onContactsFilterClear"
      />
      <header class="sz-contacts-list-header">
        <div class="sz-contacts-list-title">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          <span class="sz-contacts-list-title-text">{{ listColumnTitle }}</span>
        </div>
        <div class="sz-contacts-list-actions">
          <!-- SZ_CONTACTS_FILTER_BADGE_20260528 / SZ_FILTER_GROUP_20260528:
               X (limpar) + funil = controle segmentado (Figma Frame 1652706356).
               Com filtro ativo os dois se juntam (cantos só nas pontas, borda
               compartilhada via margin -1.5px); sem filtro, só o funil arredondado. -->
          <div
            class="sz-contacts-filter-group"
            :class="{ 'sz-cfg--joined': activeFilterCount > 0 }"
          >
            <button
              v-if="activeFilterCount > 0"
              type="button"
              class="sz-contacts-list-iconbtn sz-contacts-list-iconbtn--clear"
              aria-label="Limpar filtros"
              title="Limpar filtros"
              @click="onContactsFilterClear"
            >
              <svg width="16" height="16" viewBox="4 4 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
            <button
              type="button"
              class="sz-contacts-list-iconbtn sz-contacts-list-iconbtn--filter"
              :class="{ 'sz-contacts-list-iconbtn--active': activeFilterCount > 0 }"
              aria-label="Filtrar"
              @click="showFilterSheet = !showFilterSheet"
            >
              <span v-if="activeFilterCount > 0" class="sz-contacts-filter-badge">{{ activeFilterCount }}</span>
              <!-- Funil Figma: viewBox 0 0 20 20 (sem crop) — os limites naturais do
                   path batem com o Vector do Figma (L/R 6.25%, top 31.25%, bottom 25%). -->
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.625 10.625C15.625 10.7908 15.5592 10.9497 15.4419 11.0669C15.3247 11.1842 15.1658 11.25 15 11.25H5C4.83424 11.25 4.67527 11.1842 4.55806 11.0669C4.44085 10.9497 4.375 10.7908 4.375 10.625C4.375 10.4592 4.44085 10.3003 4.55806 10.1831C4.67527 10.0658 4.83424 10 5 10H15C15.1658 10 15.3247 10.0658 15.4419 10.1831C15.5592 10.3003 15.625 10.4592 15.625 10.625ZM18.125 6.25H1.875C1.70924 6.25 1.55027 6.31585 1.43306 6.43306C1.31585 6.55027 1.25 6.70924 1.25 6.875C1.25 7.04076 1.31585 7.19973 1.43306 7.31694C1.55027 7.43415 1.70924 7.5 1.875 7.5H18.125C18.2908 7.5 18.4497 7.43415 18.5669 7.31694C18.6842 7.19973 18.75 7.04076 18.75 6.875C18.75 6.70924 18.6842 6.55027 18.5669 6.43306C18.4497 6.31585 18.2908 6.25 18.125 6.25ZM11.875 13.75H8.125C7.95924 13.75 7.80027 13.8158 7.68306 13.9331C7.56585 14.0503 7.5 14.2092 7.5 14.375C7.5 14.5408 7.56585 14.6997 7.68306 14.8169C7.80027 14.9342 7.95924 15 8.125 15H11.875C12.0408 15 12.1997 14.9342 12.3169 14.8169C12.4342 14.6997 12.5 14.5408 12.5 14.375C12.5 14.2092 12.4342 14.0503 12.3169 13.9331C12.1997 13.8158 12.0408 13.75 11.875 13.75Z" fill="currentColor"/>
              </svg>
            </button>
          </div>

          <!-- SZ: botão de ordenação — pill com ícone + label (igual ao
               ConversationBasicFilter da lista de chats). Click abre dropdown. -->
          <div class="sz-contacts-sort-wrap">
            <div
              class="sz-sortbtn"
              role="button"
              tabindex="0"
              :aria-label="`Ordenar: ${sortLabel} (clique para alternar)`"
              @click="cycleSort"
              @keydown.enter.prevent="cycleSort"
              @keydown.space.prevent="cycleSort"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21 16-4 4-4-4"/><path d="M17 20V4"/><path d="m3 8 4-4 4 4"/><path d="M7 4v16"/></svg>
              <span class="sz-sortbtn-label">{{ sortLabel }}</span>
            </div>
          </div>
        </div>
      </header>

      <div class="sz-contacts-list-scroll">
        <!-- SZ: skeleton no load inicial (substitui o Spinner) — 8 cards pra
             preencher a viewport até a lista real chegar. -->
        <div v-if="isFetchingList" class="sz-contacts-skeleton-list">
          <div v-for="n in 8" :key="n" class="sz-contacts-skeleton-card">
            <span class="sz-cs-avatar" />
            <div class="sz-cs-body">
              <span class="sz-cs-line sz-cs-line--name" />
              <span class="sz-cs-line sz-cs-line--phone" />
              <span class="sz-cs-line sz-cs-line--labels" />
            </div>
          </div>
        </div>
        <template v-else>
          <ContactsBulkActionBar
            v-if="hasSelection"
            :visible-contact-ids="visibleContactIds"
            :selected-contact-ids="selectedContactIds"
            :is-loading="isBulkActionLoading"
            @toggle-all="toggleSelectAll"
            @clear-selection="clearSelection"
            @assign-labels="assignLabels"
            @delete-selected="openBulkDeleteDialog"
          />
          <div v-if="showEmptyText && !showEmptyStateLayout" class="flex items-center justify-center py-10">
            <span class="text-base text-n-slate-11">{{ emptyStateMessage }}</span>
          </div>
          <ContactsList
            v-else-if="hasContacts"
            :contacts="contacts"
            :selected-contact-ids="selectedContactIds"
            @toggle-contact="toggleContactSelection"
          />
          <!-- SZ: skeleton dos cards seguintes enquanto carrega a próxima página -->
          <div v-if="isFetchingMore" class="sz-contacts-skeleton-list">
            <div v-for="n in 3" :key="n" class="sz-contacts-skeleton-card">
              <span class="sz-cs-avatar" />
              <div class="sz-cs-body">
                <span class="sz-cs-line sz-cs-line--name" />
                <span class="sz-cs-line sz-cs-line--phone" />
                <span class="sz-cs-line sz-cs-line--labels" />
              </div>
            </div>
          </div>
          <!-- SZ: sentinel pra IntersectionObserver — quando entra na viewport,
               dispara loadMore() pra carregar a próxima página. -->
          <div
            v-if="hasContacts && hasMorePages"
            ref="infiniteScrollSentinel"
            class="sz-contacts-list-sentinel"
            aria-hidden="true"
          />
        </template>
      </div>
    </aside>

    <!-- SZ: paginação substituída por scroll infinito (sentinel no fim da lista) -->

    <!-- Coluna 2: Empty (sem contato) OU router-view (com :contactId) -->
    <main class="sz-contacts-center-col">
      <router-view v-if="$route.params.contactId" />
      <SzContactsEmpty
        v-else
        @create-contact="openCreateNewContactDialog"
        @import-contacts="openImportContactsModal"
      />
    </main>

    <Dialog
      v-if="selectedCount"
      ref="bulkDeleteDialogRef"
      type="alert"
      :title="bulkDeleteDialogTitle"
      :description="bulkDeleteDialogDescription"
      :confirm-button-label="bulkDeleteDialogConfirmLabel"
      :is-loading="isBulkActionLoading"
      @confirm="deleteContacts"
    />

    <!-- SZ_CONTACTS_EMPTY_FIX_20260525: modais disparados pelos cards do empty state -->
    <CreateNewContactDialog ref="createNewContactDialogRef" @create="createContact" />
    <SzImportContactsModal ref="szImportContactsModalRef" @imported="onContactsImported" />
  </section>
</template>

<style scoped>
.sz-contacts-layout {
  font-family: 'Inter', system-ui, sans-serif;
}

/* Coluna 1: lista de contatos */
.sz-contacts-list-col {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 400px;
  min-width: 400px;
  max-width: 400px;
  height: 100%;
  background: #FFFFFF;
  border-right: 1px solid #E5E7EB;
  box-sizing: border-box;
  /* Ancoragem do SzFilterSheet (que usa position:absolute / left:0 right:0) */
  position: relative;
}
.dark .sz-contacts-list-col {
  background: #1D1E21;
  border-right-color: #2A2C30;
}

/* Header da lista */
.sz-contacts-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  height: 56px;
  border-bottom: 1px solid #E5E7EB;
  box-sizing: border-box;
  flex-shrink: 0;
}
.dark .sz-contacts-list-header {
  border-bottom-color: #2A2C30;
}
.sz-contacts-list-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #000000;
  min-width: 0;
}
.sz-contacts-list-title-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dark .sz-contacts-list-title { color: #FFFFFF; }
.sz-contacts-list-title svg { color: #525252; }
.dark .sz-contacts-list-title svg { color: #9797A0; }

.sz-contacts-list-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}
/* SZ_FILTER_GROUP_20260528: X + funil como controle segmentado (Figma). */
.sz-contacts-filter-group { display: flex; align-items: center; }
.sz-cfg--joined .sz-contacts-list-iconbtn--clear { border-radius: 8px 0 0 8px; }
.sz-cfg--joined .sz-contacts-list-iconbtn--filter { border-radius: 0 8px 8px 0; margin-left: -1.5px; }
/* a cell em hover/foco vem pra frente pra não cortar a borda compartilhada */
.sz-contacts-filter-group .sz-contacts-list-iconbtn:hover,
.sz-contacts-filter-group .sz-contacts-list-iconbtn:focus-visible { z-index: 1; }
/* SZ: replica EXATAMENTE o .sz-chl-iconbtn da home (transparent + border 1.5px). */
.sz-contacts-list-iconbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1.5px solid #D4D4D4;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  color: #000000;
  transition: background 0.15s, border-color 0.15s;
  flex-shrink: 0;
  position: relative;
  box-sizing: border-box;
}
.sz-contacts-list-iconbtn:hover { background: rgba(0, 0, 0, 0.03); }
/* SZ_CONTACTS_FILTER_BADGE_20260528 — Figma: 16px, #30833C, Inter 500/12px */
.sz-contacts-filter-badge {
  position: absolute;
  top: -4px;
  right: -5px;
  min-width: 16px;
  height: 16px;
  padding: 0 3px;
  background: #30833C;
  color: #FFFFFF;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  pointer-events: none;
}
.sz-contacts-list-iconbtn--clear { color: #000000; }
.dark .sz-contacts-list-iconbtn--clear { color: #FFFFFF; }
.sz-contacts-list-iconbtn:focus-visible {
  outline: 2px solid #388E3C;
  outline-offset: 1px;
  z-index: 1;
}
.sz-contacts-list-iconbtn--active {
  border-color: #D4D4D4;
}
.dark .sz-contacts-list-iconbtn {
  border-color: #303138;
  color: #FFFFFF;
  background: #1D1E21;
}
.dark .sz-contacts-list-iconbtn:hover { background: #26282B; }
.dark .sz-contacts-list-iconbtn--active { border-color: #303138; }

/* SZ_FILTER_ICON_SIZE_20260528: vence o global "button svg{width:14px!important}" */
.sz-contacts-list-iconbtn--filter svg { width: 20px !important; height: 20px !important; }
.sz-contacts-list-iconbtn--clear svg { width: 16px !important; height: 16px !important; }

.sz-contacts-filter-icon {
  width: 20px;
  height: 20px;
  display: inline-block;
}

/* SZ: pill do botão de sort — igual ao ConversationBasicFilter (Figma) */
.sz-sortbtn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 4px 8px 4px 4px;
  background: #FFFFFF;
  border: 1.5px solid #E5E5E5;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  box-sizing: border-box;
  color: #3F3F3F;
  flex: none;
  transition: background 0.15s, border-color 0.15s;
}
.sz-sortbtn:hover { background: rgba(0, 0, 0, 0.03); }
.sz-sortbtn:focus-visible {
  outline: 2px solid #388E3C;
  outline-offset: 1px;
}
.sz-sortbtn--active { background: rgba(0, 0, 0, 0.04); border-color: #BDBDBD; }
.sz-sortbtn-label {
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  color: inherit;
  white-space: nowrap;
}
.dark .sz-sortbtn {
  background: #1D1E21;
  border-color: #303138;
  color: #FFFFFF;
}
.dark .sz-sortbtn:hover { background: #26282B; }
.dark .sz-sortbtn--active { background: #26282B; border-color: #4A4D52; }
.dark .sz-sortbtn-label { color: #FFFFFF; }

/* SZ: sort dropdown */
.sz-contacts-sort-wrap {
  position: relative;
}
.sz-contacts-sort-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 50;
  min-width: 240px;
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  box-shadow: 0 6px 24px rgba(15, 23, 42, 0.12);
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.sz-contacts-sort-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 10px;
  background: transparent;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #1F2937;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  transition: background 0.1s ease;
  font-family: inherit;
}
.sz-contacts-sort-item:hover { background: #F3F4F6; }
.sz-contacts-sort-item--active { background: #E6F2E6; color: #2E7D32; }
.sz-contacts-sort-sep {
  height: 1px;
  background: #E5E7EB;
  margin: 4px 6px;
}
.dark .sz-contacts-sort-menu {
  background: #1D1E21;
  border-color: #3D3F43;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.5);
}
.dark .sz-contacts-sort-item { color: #E5E7EB; }
.dark .sz-contacts-sort-item:hover { background: #242528; }
.dark .sz-contacts-sort-item--active { background: rgba(89, 170, 89, 0.18); color: #8FD18F; }
.dark .sz-contacts-sort-sep { background: #3D3F43; }

/* Scroll da lista */
.sz-contacts-list-scroll {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

/* Sentinel do IntersectionObserver (scroll infinito) */
.sz-contacts-list-sentinel {
  height: 1px;
}

/* Skeleton dos cards enquanto carrega próxima página */
.sz-contacts-skeleton-list {
  display: flex;
  flex-direction: column;
}
.sz-contacts-skeleton-card {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  background: #FFFFFF;
  border-bottom: 1px solid #E5E7EB;
}
.dark .sz-contacts-skeleton-card {
  background: #1D1E21;
  border-bottom-color: #242528;
}
.sz-cs-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #E5E7EB;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
}
.sz-cs-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
  padding-top: 4px;
}
.sz-cs-line {
  display: block;
  height: 12px;
  border-radius: 6px;
  background: #E5E7EB;
  position: relative;
  overflow: hidden;
}
.sz-cs-line--name  { width: 60%; }
.sz-cs-line--phone { width: 40%; }
.sz-cs-line--labels { width: 75%; height: 16px; border-radius: 999px; }

.dark .sz-cs-avatar,
.dark .sz-cs-line { background: #2A2C30; }

/* Shimmer animation — gradient passando da esquerda pra direita */
.sz-cs-avatar::after,
.sz-cs-line::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.55) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  transform: translateX(-100%);
  animation: sz-cs-shimmer 1.3s infinite;
}
.dark .sz-cs-avatar::after,
.dark .sz-cs-line::after {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.08) 50%,
    rgba(255, 255, 255, 0) 100%
  );
}
@keyframes sz-cs-shimmer {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

/* Footer com paginação */
.sz-contacts-list-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border-top: 1px solid #E5E7EB;
  font-size: 12px;
  color: #525252;
  flex-shrink: 0;
}
.dark .sz-contacts-list-footer {
  border-top-color: #2A2C30;
  color: #9797A0;
}

/* Coluna 2: central grande */
.sz-contacts-center-col {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  display: flex;
}
</style>
